import {Injectable, WritableSignal, computed, inject, signal} from '@angular/core'
import {Store} from '@ngrx/store'
import {Subject, filter, takeUntil} from 'rxjs'
import {ArrayUtil} from '@common/utils/array.util'
import {StringUtil} from '@common/utils/string.util'
import {getPermissionsSelector} from '@core/store/core.selectors'
import {MenuOptions, Permision, PermisionsType} from '@models/pemissions.models'
import {havePermission} from '@pages/home/home.utils'
import {TranslateStringV2Pipe} from '@pipes/translate-string-v2.pipe'
import {CustomTablesService} from '@service/custom-tables.service'
import {CustomTermsService} from '@service/custom-terms.service'
import {PermissionsService} from '@service/permissions/permissions.service'

/**
 * Tipo que define un elemento de navegación del menú.
 *
 * @description
 * Estructura completa de un elemento del menú de navegación, incluyendo
 * identificador, título, URL, icono, visibilidad por dispositivo y acción opcional.
 */
export type MenuNavigationItem = {
  id: MenuOptions
  title: string
  url: string
  icon: string
  mobile: WritableSignal<boolean>
  tablet: WritableSignal<boolean>
  disableOffline: WritableSignal<boolean>
  action?: () => void
}

/**
 * Servicio para gestionar el menú de navegación de la aplicación.
 *
 * @description
 * Proporciona funcionalidad completa para construir y gestionar el menú de navegación
 * basándose en permisos del usuario, feature flags y configuraciones personalizadas.
 * El menú se actualiza dinámicamente según los permisos del usuario y puede incluir
 * elementos de tablas personalizadas, stocks, términos y otros elementos dinámicos.
 * Proporciona un signal reactivo con los elementos del menú actualizados. Útil para
 * construir menús adaptativos que cambian según permisos y configuración.
 *
 * @usageNotes
 *
 * ### Uso básico
 *
 * ```typescript
 * // Inyectar el servicio
 * constructor(private menuService: MenuService) {}
 *
 * // Inicializar menú (llamar al iniciar la app)
 * this.menuService.init();
 *
 * // Obtener elementos del menú (signal reactivo)
 * const menuItems = this.menuService.navigate();
 *
 * // Destruir servicio (llamar al destruir componente)
 * this.menuService.destroy();
 * ```
 *
 * ### Casos de uso
 *
 * ```typescript
 * // En componente de menú
 * export class MenuComponent implements OnInit, OnDestroy {
 *   constructor(private menuService: MenuService) {}
 *
 *   ngOnInit() {
 *     this.menuService.init();
 *   }
 *
 *   ngOnDestroy() {
 *     this.menuService.destroy();
 *   }
 *
 *   getMenuItems(): MenuNavigationItem[] {
 *     return this.menuService.navigate();
 *   }
 * }
 *
 * // Filtrar elementos visibles
 * const visibleItems = this.menuService.navigate().filter(item =>
 *   item.mobile() || item.tablet()
 * );
 * ```
 *
 * ### Best Practices
 *
 * - Llamar `init()` al iniciar la aplicación para construir el menú
 * - Llamar `destroy()` al destruir el componente para limpiar suscripciones
 * - El menú se actualiza automáticamente cuando cambian los permisos
 * - Los elementos se filtran automáticamente por permisos y feature flags
 * - Usar el signal `navigate()` para acceso reactivo a los elementos
 *
 * ### Bad Uses / Errores Comunes
 *
 * ```typescript
 * // ❌ NO: Modificar elementos del menú directamente
 * const items = this.menuService.navigate();
 * items[0].mobile.set(false); // Puede causar inconsistencias
 *
 * // ✅ SÍ: El servicio gestiona la visibilidad automáticamente
 * // Los cambios se hacen a través de permisos y feature flags
 *
 * // ❌ NO: No inicializar el servicio
 * const items = this.menuService.navigate(); // Puede estar vacío
 *
 * // ✅ SÍ: Inicializar primero
 * this.menuService.init();
 * const items = this.menuService.navigate();
 * ```
 *
 * @example
 * ```typescript
 * export class SideMenuComponent implements OnInit, OnDestroy {
 *   constructor(private menuService: MenuService) {}
 *
 *   ngOnInit() {
 *     this.menuService.init();
 *   }
 *
 *   ngOnDestroy() {
 *     this.menuService.destroy();
 *   }
 *
 *   getMenuItems(): MenuNavigationItem[] {
 *     return this.menuService.navigate().filter(item =>
 *       item.mobile() // Solo elementos visibles en móvil
 *     );
 *   }
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class MenuService {
  // Signals
  private readonly _navigate = signal<MenuNavigationItem[]>([])
  navigate = computed(() => this._navigate())

  // Private properties
  private firstLoadPermissionSelector = true
  private canSeeReport = false
  private canReadOrders = false
  private canSeePayments = false
  private readonly destroyed$ = new Subject<void>()

  // Dependency injections
  private readonly store = inject(Store)
  private readonly customTablesService = inject(CustomTablesService)
  private readonly translateStringV2Pipe = inject(TranslateStringV2Pipe)
  private readonly permissionService = inject(PermissionsService)
  private readonly customTermsService = inject(CustomTermsService)

  /**
   * Inicializa el menú y se suscribe a los cambios de permisos del usuario.
   *
   * @description
   * Carga los elementos por defecto del menú y se suscribe al selector de permisos del
   * store. En la primera emisión, reconstruye el menú tomando en cuenta los permisos
   * actuales (reportes, pedidos y pagos) y añade entradas dinámicas (tablas personalizadas,
   * stocks y términos). En emisiones posteriores de permisos, reconstruye el menú desde cero.
   */
  init = (): void => {
    this._navigate.set(this.getDefaultMenuItems())
    this.store
      .select(getPermissionsSelector)
      .pipe(
        takeUntil(this.destroyed$),
        filter(res => res?.length > 0)
      )
      .subscribe(res => {
        if (!this.firstLoadPermissionSelector) {
          this._navigate.set(this.getDefaultMenuItems())
        }

        this.canSeeReport = havePermission(PermisionsType.REPORTS, Permision.CAN_READ, res)
        this.canReadOrders = havePermission(PermisionsType.ORDERS, Permision.CAN_READ, res)
        this.canSeePayments = havePermission(PermisionsType.PAYMENTS, Permision.CAN_READ, res)
        this.managePermissionsOnMenu()

        this.loadMoreMenuItems()

        this.firstLoadPermissionSelector = false
      })
  }

  /**
   * Libera la suscripción al store y limpia los elementos del menú.
   *
   * @description
   * Completa el `Subject` `destroyed$` para cancelar la suscripción creada en `init()`
   * y vacía el signal `_navigate`. Debe invocarse cuando la pantalla contenedora del
   * menú se destruye (por ejemplo, al cerrar sesión).
   */
  destroy = (): void => {
    this.destroyed$.next()
    this.destroyed$.complete()
    this._navigate.set([])
  }

  private readonly managePermissionsOnMenu = (): void => {
    const permissionsConfig = {
      [MenuOptions.REPORTS]: this.canSeeReport,
      [MenuOptions.ORDERS]: this.canReadOrders,
      [MenuOptions.CLOSING_DAY]: this.canReadOrders,
      [MenuOptions.GENERAL_DEBTS]: this.canSeePayments,
      [MenuOptions.GENERAL_PAYMENTS]: this.canSeePayments,
    }

    this._navigate.update(prev =>
      prev.map(menuItem => {
        const permission = permissionsConfig[menuItem.id]
        if (permission !== undefined) {
          menuItem.mobile.set(permission)
          menuItem.tablet.set(permission)
        }
        return menuItem
      })
    )
  }

  private readonly loadMoreMenuItems = async (): Promise<void> => {
    await this.getTabsMenu()
    this.getStocksMenu()
    this.getTermsMenu()

    // Prevent duplicate menu items
    this._navigate.update(prev =>
      prev.filter((item, index, self) => self.findIndex(t => t.id === item.id && item.url === t.url) === index)
    )
  }

  private readonly getTabsMenu = async (): Promise<void> => {
    const data = await this.customTablesService.getTableMenuItems().then(res => {
      const {group, data} = res

      const permissions = this.permissionService.getLocalPermissions()

      const permissionIds = ArrayUtil.extractColumn(permissions, 'id')

      const tabsMenuItems = []

      if (group && permissionIds.includes(PermisionsType.PUBLIC_TABLE_MANAGEMENT)) {
        tabsMenuItems.push({
          id: MenuOptions.TABLE_MANAGEMENT,
          title: 'maintenance',
          url: '/tables-management',
          icon: 'assets/icon/tables-outline.svg',
          mobile: signal(true),
          tablet: signal(true),
          disableOffline: signal(true),
        })
      }

      const tabs = data.map(item => {
        const name = this.translateStringV2Pipe.transform(item.name)

        return {
          id: MenuOptions.TABLE_MANAGEMENT,
          title: `${name}`,
          url: `/tables-management/${item.id}?isGrouped=0`,
          icon: 'assets/icon/tables-outline.svg',
          mobile: signal(true),
          tablet: signal(true),
          disableOffline: signal(true),
        }
      })

      return [...tabsMenuItems, ...tabs]
    })

    if (data.length > 0) {
      this._navigate.update(prev => [...prev, ...data])
    }
  }

  private readonly getStocksMenu = (): void => {
    const {canReadStocks} = this.permissionService

    if (canReadStocks) {
      const option = {
        id: MenuOptions.STOCKS,
        title: 'stocks',
        url: '/stocks',
        icon: 'assets/icon/boxes-stacked-light.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(true),
      }

      this._navigate.update(prev => [...prev, option])
    }
  }

  private readonly getDefaultMenuItems = (): MenuNavigationItem[] => {
    return [
      {
        id: MenuOptions.HOME,
        title: 'outlets',
        url: '/home',
        icon: 'assets/icon/store.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
      },
      {
        id: MenuOptions.SCHEDULE,
        title: 'my_schedule',
        url: '/schedule',
        icon: 'assets/icon/calendar-circle-user.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
      },
      {
        id: MenuOptions.ACTIVITIES,
        title: 'activities_performed',
        url: '/activities',
        icon: 'assets/icon/clipboard-check.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
      },
      {
        id: MenuOptions.ORDERS,
        title: 'orders_placed',
        url: '/orders',
        icon: 'assets/icon/cart-shopping.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
      },
      {
        id: MenuOptions.GENERAL_DEBTS,
        title: 'general_debts',
        url: '/general-debts',
        icon: 'assets/icon/cash-register.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
      },
      {
        id: MenuOptions.GENERAL_PAYMENTS,
        title: 'general_payments_mobile',
        url: '/general-payments',
        icon: 'assets/icon/general-payments-light.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
      },
      {
        id: MenuOptions.CLOSING_DAY,
        title: 'day_summary',
        url: '/closing-day-tabs',
        icon: 'assets/icon/calendar-circle-info.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
      },
      {
        id: MenuOptions.REPORTS,
        title: 'reports',
        url: '/reports',
        icon: 'assets/icon/file-chart-pie.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(true),
      },
      {
        id: MenuOptions.GALLERY,
        title: 'gallery',
        url: '/gallery',
        icon: 'assets/icon/image.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(true),
      },
    ]
  }

  private readonly getTermsMenu = (): void => {
    const terms = this.customTermsService.getConfigCustomTerms()

    if (terms && StringUtil.hasValue(terms?.description)) {
      const option = {
        id: MenuOptions.TERMS,
        title: terms?.title ? this.translateStringV2Pipe.transform(terms?.title) : 'title_terms_geo',
        url: 'terms',
        icon: 'assets/icon/circle-info.svg',
        mobile: signal(true),
        tablet: signal(true),
        disableOffline: signal(false),
        action: () => this.customTermsService.openModalTerms(true),
      }

      this._navigate.update(prev => [...prev, option])
    }
  }
}
